import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Package, ArrowRight, Home, Phone, 
  CheckCircle, Clock, Truck, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';

const STATUS_STEPS = [
  { key: 'pending', label: 'Received', icon: CheckCircle },
  { key: 'assigned', label: 'Assigned', icon: CheckCircle },
  { key: 'in_progress', label: 'In Progress', icon: Truck },
  { key: 'completed', label: 'Completed', icon: CheckCircle }
];

const BookingTracker = () => {
  const navigate = useNavigate();
  const [referenceNumber, setReferenceNumber] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!referenceNumber.trim()) {
      setError('Please enter a reference number');
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const response = await api.get(`/bookings/lookup?ref=${referenceNumber.trim().toUpperCase()}`);
      setBooking(response.data);
    } catch (err) {
      setError('Booking not found. Please check your reference number and try again.');
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIndex = (status) => {
    const index = STATUS_STEPS.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  const currentStep = booking ? getStatusIndex(booking.status) : -1;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="booking-tracker-page">
      {/* Header */}
      <header className="bg-[#111] border-b border-white/10 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center text-black font-bold text-sm">
              H
            </div>
            <span className="font-semibold">NC Help Hub</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/60">
              <Home className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="px-4 py-8 max-w-lg mx-auto">
        {/* Search Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Track Your Booking</h1>
          <p className="text-white/60">Enter your reference number to check status</p>
        </motion.div>

        <motion.form
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSearch}
          className="mb-8"
        >
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="e.g., NCH-2026-123456"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value.toUpperCase())}
                className="pl-10 h-12 bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/40 text-lg tracking-wider"
                data-testid="booking-tracker-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="h-12 px-6 bg-[#D4AF37] text-black hover:bg-[#E6C965]"
              data-testid="booking-tracker-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowRight className="w-5 h-5" />
              )}
            </Button>
          </div>
          {error && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm mt-2 flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4" />
              {error}
            </motion.p>
          )}
        </motion.form>

        {/* Results */}
        {booking && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-4"
          >
            {/* Status Card */}
            <div className="bg-[#1a1a1a] rounded-2xl p-5 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white/50 text-sm">Reference</p>
                  <p className="text-xl font-bold text-[#D4AF37]">{booking.reference_number}</p>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  booking.status === 'completed' 
                    ? 'bg-green-500/20 text-green-400'
                    : booking.status === 'in_progress'
                    ? 'bg-purple-500/20 text-purple-400'
                    : booking.status === 'assigned'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {booking.status?.replace('_', ' ')}
                </div>
              </div>

              {/* Progress Steps */}
              <div className="relative">
                <div className="flex justify-between mb-2">
                  {STATUS_STEPS.map((step, idx) => {
                    const isActive = idx <= currentStep;
                    const isCurrent = idx === currentStep;
                    return (
                      <div key={step.key} className="flex flex-col items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-colors ${
                          isActive 
                            ? isCurrent 
                              ? 'bg-[#D4AF37] text-black' 
                              : 'bg-green-500 text-white'
                            : 'bg-white/10 text-white/40'
                        }`}>
                          <step.icon className="w-4 h-4" />
                        </div>
                        <span className={`text-xs text-center ${isActive ? 'text-white' : 'text-white/40'}`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Progress Line */}
                <div className="absolute top-4 left-4 right-4 h-0.5 bg-white/10 -z-10">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${(currentStep / (STATUS_STEPS.length - 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="bg-[#1a1a1a] rounded-2xl p-5 border border-white/10">
              <h3 className="font-semibold mb-4">Booking Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Service</span>
                  <span className="font-medium">{booking.service_name || booking.service_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Date</span>
                  <span>{booking.scheduled_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Time</span>
                  <span>{booking.scheduled_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Location</span>
                  <span className="text-right max-w-[60%]">{booking.city}, {booking.state}</span>
                </div>
                {booking.worker_name && (
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <span className="text-white/50">Your Provider</span>
                    <span className="font-medium text-[#D4AF37]">{booking.worker_name}</span>
                  </div>
                )}
                {booking.total_amount > 0 && (
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <span className="text-white/50">Total</span>
                    <span className="font-bold text-lg text-[#D4AF37]">${booking.total_amount?.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* View Full Confirmation */}
            <Button
              onClick={() => navigate(`/booking/confirmation/${booking.id}`)}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E6C965] h-12"
            >
              View Full Confirmation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* Empty State */}
        {searched && !booking && !loading && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-8"
          >
            <p className="text-white/50">No booking found</p>
          </motion.div>
        )}

        {/* Help Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-white/50 text-sm mb-3">Can't find your booking?</p>
          <a 
            href="tel:+12529302698" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
          >
            <Phone className="w-4 h-4" />
            Call (252) 930-2698
          </a>
        </motion.div>
      </main>
    </div>
  );
};

export default BookingTracker;
